import React, { useState, useEffect } from 'react'
import { Modal, Button, ModalBody, ModalFooter, ModalHeader, Row, Col } from "reactstrap";
import CustomInput from '../../common/CustomInput';
import SelectInput from '../../common/SelectInput';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import customAxios from '../../../customAxios';
import { toast } from "react-toastify";

const AddEditDeliveryChargeModal = ({ openModal, onCloseModal, editData, title }) => {
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);

    const schema = yup.object({
        country: yup.object().required('Country is required').nullable(),
        min_order_amount: yup.number()
            .transform((value, originalValue) => (originalValue === '' ? undefined : Number(originalValue)))
            .positive('Min order amount must be greater than 0')
            .required('Min order amount is required'),
        delivery_charge: yup.number()
            .transform((value, originalValue) => (originalValue === '' ? undefined : Number(originalValue)))
            .positive('Delivery charge must be greater than 0')
            .required('Delivery charge is required'),
    })

    const { register, handleSubmit, control, formState: { errors }, reset, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            country: null,
            min_order_amount: '',
            delivery_charge: ''
        }
    });

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await customAxios.get('auth/countries');
                const countriesData = response?.data?.data || response?.data || [];

                // Transform countries to SelectInput format {label, value}
                const formattedCountries = countriesData.map((country) => ({
                    label: country.name || country.country || country,
                    value: country.name || country.country || country
                }));

                setCountries(formattedCountries);
            } catch (err) {
                toast.error(err?.response?.data?.message || err?.message || "Failed to fetch countries");
            }
        };

        fetchCountries();
    }, []);

    useEffect(() => {
        if (editData && countries.length > 0) {
            // Find the country object that matches the editData country
            const countryObj = countries.find(
                (c) => c.value === editData?.country || c.label === editData?.country
            );

            if (countryObj) {
                setValue("country", countryObj);
                setSelectedCountry(countryObj);
            }
            setValue("min_order_amount", editData?.min_order_amount || '');
            setValue("delivery_charge", editData?.delivery_charge || '');
        } else if (!editData) {
            reset();
            setSelectedCountry(null);
        }
    }, [editData, setValue, reset, countries])

    const clearclose = () => {
        onCloseModal()
        reset()
        setSelectedCountry(null)
    }

    const handleCountryChange = (selectedOption) => {
        setSelectedCountry(selectedOption);
        setValue("country", selectedOption);
    };

    const Submit = async (data) => {
        try {
            setLoading(true);
            const payload = {
                country: data.country?.value || data.country,
                min_order_amount: data.min_order_amount,
                delivery_charge: data.delivery_charge
            };

            if (editData) {
                // Update
                await customAxios.post(`admin/delivery-charges/${editData._id}`, payload);
                toast.success(`Updated Successfully`);
            } else {
                // Create
                await customAxios.post('admin/delivery-charges', payload);
                toast.success(`Created Successfully`);
            }
            clearclose()
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || "Failed to save delivery charge");
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal isOpen={openModal} toggle={clearclose} size="lg">
            <ModalHeader toggle={clearclose}>
                <h5
                    className="modal-title f-w-600"
                    id="exampleModalLabel2"
                >
                    {title}
                </h5>
            </ModalHeader>
            <ModalBody>
                <form>
                    <Row>
                        <Col xl="12">
                            <SelectInput
                                fieldLabel='Country'
                                control={control}
                                error={errors.country}
                                fieldName='country'
                                options={countries}
                                selectvalue={selectedCountry}
                                onChangeValue={handleCountryChange}
                            />
                        </Col>
                        <Col xl="12">
                            <CustomInput
                                fieldLabel='Min Order Amount'
                                control={control}
                                error={errors.min_order_amount}
                                fieldName='min_order_amount'
                                type="number"
                                placeholder="Enter minimum order amount"
                            />
                        </Col>
                        <Col xl="12">
                            <CustomInput
                                fieldLabel='Delivery Charge'
                                control={control}
                                error={errors.delivery_charge}
                                fieldName='delivery_charge'
                                type="number"
                                placeholder="Enter delivery charge"
                            />
                        </Col>
                    </Row>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button
                    type="button"
                    color="secondary"
                    onClick={clearclose}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    color="primary"
                    onClick={handleSubmit(Submit)}
                    disabled={loading}
                >
                    {loading ? "Saving..." : "Save"}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default AddEditDeliveryChargeModal

